import React from 'react'
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts'
import { Box, Typography } from '@mui/material'
import { Spinner, theme } from '@ketch-com/deck'
import pluralize from 'pluralize'

const getLabel = (count: number, totalCount: number): string => {
  if (count === 0) return 'No Data Yet'
  const percentage = (count / totalCount) * 100
  if (percentage === 0) return 'No Data Yet'
  if (percentage < 10) return 'Minimal'
  if (percentage < 30) return 'Restricted'
  if (percentage < 60) return 'Moderate'
  if (percentage < 90) return 'Wide'
  return 'Extensive'
}
interface GradientDefsProps {
  id: string
}
const GradientDefs = ({ id }: GradientDefsProps) => {
  const gradients = {
    minimal: (
      <linearGradient id={`gradient-minimal`} x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#40FF00" />
        <stop offset="100%" stopColor="#00F1D4" />
      </linearGradient>
    ),
    restricted: (
      <linearGradient id={`gradient-restricted`} x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#40FF00" />
        <stop offset="50%" stopColor="#00F1D4" />
        <stop offset="100%" stopColor="#0077FF" />
      </linearGradient>
    ),
    moderate: (
      <linearGradient id={`gradient-moderate`} x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#40FF00" />
        <stop offset="33%" stopColor="#00F1D4" />
        <stop offset="66%" stopColor="#0077FF" />
        <stop offset="100%" stopColor="#FB2AFF" />
      </linearGradient>
    ),
    wide: (
      <linearGradient id={`gradient-wide`} x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#40FF00" />
        <stop offset="25%" stopColor="#00F1D4" />
        <stop offset="50%" stopColor="#0077FF" />
        <stop offset="75%" stopColor="#FB2AFF" />
        <stop offset="100%" stopColor="#FF6A00" />
      </linearGradient>
    ),
    extensive: (
      <linearGradient id={`gradient-extensive`} x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#40FF00" />
        <stop offset="20%" stopColor="#00F1D4" />
        <stop offset="40%" stopColor="#0077FF" />
        <stop offset="55%" stopColor="#FB2AFF" />
        <stop offset="70%" stopColor="#FF020D" />
        <stop offset="85%" stopColor="#FF6A00" />
        <stop offset="100%" stopColor="#FFD900" />
      </linearGradient>
    ),
  }

  return (
    <svg width="0" height="0">
      <defs>{gradients[id as keyof typeof gradients]}</defs>
    </svg>
  )
}

interface SpectrumRechartsProps {
  categoryCount: number
  maxCategoryCount?: number
  isLoading?: boolean
}

export const OverAllSpectrum = ({ categoryCount, maxCategoryCount = 0, isLoading }: SpectrumRechartsProps) => {
  const labelBgColor = theme.palette.Black.o24
  const spectrumData = [{ value: 100 }]
  const label = getLabel(categoryCount, maxCategoryCount)
  const gradientId = `gradient-${label.toLowerCase()}`
  const spectrumColor = `url(#${gradientId})`

  return (
    <Box
      sx={{
        flex: 1,
        width: '100%',
        minWidth: 400,
        maxWidth: 628,
        height: 266,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: theme.palette.Common.Container,
        borderRadius: '11px',
        padding: '16px 20px 0 20px',
      }}
    >
      <Typography variant="h4">Overall Data Collection Spectrum</Typography>

      {isLoading ? (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Spinner size="32px" thickness={2} />
        </Box>
      ) : categoryCount === 0 ? (
        <Box
          sx={{
            position: 'relative',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-end',
          }}
        >
          <GradientDefs id="extensive" />
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={[{ value: 100 }]}
                startAngle={180}
                endAngle={0}
                dataKey="value"
                cy="103%"
                innerRadius={135}
                outerRadius={185}
                stroke="none"
                fill="url(#gradient-extensive)"
                isAnimationActive={false}
              />
              <Pie
                data={[{ value: 100 }]}
                dataKey="value"
                startAngle={180}
                endAngle={0}
                cy="103%"
                innerRadius={137}
                outerRadius={183}
                stroke="none"
                fill={theme.palette.Common.Container}
                isAnimationActive={false}
              />
            </PieChart>
          </ResponsiveContainer>

          <Box
            sx={{
              position: 'absolute',
              top: '27%',
              left: '50%',
              transform: 'translateX(-50%)',
              textAlign: 'center',
              zIndex: 1,
            }}
          >
            <Typography variant="h3" sx={{ color: theme.palette.Text.Secondary }}>
              No Data Yet
            </Typography>
          </Box>
        </Box>
      ) : (
        <>
          <GradientDefs id={label.toLowerCase()} />
          <Box
            sx={{
              position: 'relative',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-end',
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart key={label}>
                <Pie
                  data={spectrumData}
                  startAngle={180}
                  endAngle={0}
                  cy="103%"
                  innerRadius={135}
                  outerRadius={185}
                  stroke="none"
                  dataKey="value"
                  isAnimationActive={true}
                >
                  <Cell key="arc" fill={spectrumColor} />
                </Pie>
              </PieChart>
            </ResponsiveContainer>

            <Box
              sx={{
                position: 'absolute',
                top: '24%',
                left: '50%',
                transform: 'translateX(-50%)',
                textAlign: 'center',
                zIndex: 10,
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 0.5,
                  color: theme.palette.Text.PrimaryLight,
                  bgcolor: labelBgColor,
                  borderRadius: 10,
                  display: 'inline-block',
                }}
              >
                <Typography variant="h3">{label}</Typography>
              </Box>
            </Box>
          </Box>
        </>
      )}
      {!isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: categoryCount === 0 ? '75%' : '65%',
            left: '30%',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            textAlign: 'center',
            width: '40%',
          }}
        >
          {categoryCount > 0 && (
            <Typography variant="smallLabel">
              {categoryCount} {pluralize('Data Category', categoryCount)}
            </Typography>
          )}
          <Typography variant="smallBody" color={theme.palette.Text.Secondary}>
            The spectrum colors represent the breadth of data categories collected.
          </Typography>
        </Box>
      )}
    </Box>
  )
}
